<script setup>
import { ref, shallowRef } from 'vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';

useSessionGuard();

// The transmutation table: each initial-grade range and the grade it
// becomes on the report card.
const headers = [
  { title: 'Transmuted grade', key: 'transmuted_grade' },
  { title: 'Initial grade range', key: 'range', sortable: false },
];
const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', '/rooms-section/getTransmutedGrade/grades/data'),
);
load();

const editing = shallowRef(null); // null = add
const formOpen = shallowRef(false);
const form = ref({ grade: null, start: null, end: null });
const blank = (v) => v === null || v === '';
const number = (v) => blank(v) || !Number.isNaN(Number(v)) || 'Enter a number.';
const notBelowStart = (v) =>
  blank(v) || blank(form.value.start) || Number(v) >= Number(form.value.start) || 'Must be at least the start of the range.';
const fields = [
  { key: 'grade', label: 'Transmuted grade', type: 'number', required: true, rules: [number], span: 2 },
  { key: 'start', label: 'Range start', type: 'number', required: true, rules: [number] },
  { key: 'end', label: 'Range end', type: 'number', required: true, rules: [number, notBelowStart] },
];
const { busy, run } = useAsyncAction();

function openForm(row = null) {
  editing.value = row;
  form.value = { grade: row?.transmuted_grade ?? null, start: row?.start_range ?? null, end: row?.end_range ?? null };
  formOpen.value = true;
}

async function save() {
  const data = {
    transmuted_grade: form.value.grade,
    start_range: String(form.value.start),
    end_range: String(form.value.end),
  };
  const ok = await run(
    () =>
      editing.value
        ? apiRequest('patch', `/rooms-section/updateTransmutedGrade/${editing.value.id}`, data)
        : apiRequest('post', '/rooms-section/transmutedGrade', data),
    { success: editing.value ? 'Grade updated.' : 'Grade added.', error: 'Could not save the grade.' },
  );
  if (ok) {
    formOpen.value = false;
    load();
  }
}
</script>

<template>
  <UiTableCard
    title="Transmuted grades"
    description="How initial grades convert to the grade on the report card."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    :searchable="false"
    :items-per-page="25"
    add-label="Add grade"
    empty-text="No transmuted grades yet."
    :row-label="(row) => `grade ${row.transmuted_grade}`"
    :row-actions="(row) => [{ title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(row) }]"
    @add="openForm()"
  >
    <template #[`item.transmuted_grade`]="{ item }">
      <span class="font-weight-medium">{{ item.transmuted_grade }}</span>
    </template>
    <template #[`item.range`]="{ item }">{{ item.start_range }} – {{ item.end_range }}</template>
  </UiTableCard>

  <UiFormDialog
    v-model="formOpen"
    :title="editing ? 'Edit transmuted grade' : 'Add transmuted grade'"
    description="Initial grades within the range become this transmuted grade."
    layout="grid"
    v-model:values="form"
    :fields="fields"
    :saving="busy"
    :submit-text="editing ? 'Save changes' : 'Add grade'"
    @submit="save"
  />
</template>
